import React, { useState, useRef, useMemo } from 'react';
import { Plus } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { useCreateTodo, useTodoLists } from '../hooks/useTodos';
import { useUsers } from '../hooks/useTodos';
import { useDialog } from '../contexts/DialogContext';
import { todoApi } from '../api/todoApi';
import { TodoList } from '../types/todo';
import { getTodayISO, getTomorrowISO, getYesterdayISO, getRandomLaterDate, getThisWeekendISO } from '../utils/dateUtils';
import './SectionTodoEntry.css';

interface SectionTodoEntryProps {
  sectionType: 'today' | 'tomorrow' | 'thisWeekend' | 'later' | 'overdue' | 'noDueDate';
  currentFilters?: { assignee: string; list: string | number; completed: 'today' | 'all' | 'hidden'; tags: string[]; search: string };
  onNewTodoCreated?: (taskId: number, title: string) => void;
  onFiltersChange?: (newFilters: { assignee: string; list: string | number; completed: 'today' | 'all' | 'hidden'; tags: string[]; search: string }) => void;
  hideIcon?: boolean;
  /** Optional explicit ISO due date override. When provided, new tasks will use this date regardless of sectionType. */
  dueDateISO?: string | null;
}

export const SectionTodoEntry: React.FC<SectionTodoEntryProps> = ({
  sectionType,
  currentFilters,
  onNewTodoCreated,
  onFiltersChange,
  hideIcon = false,
  dueDateISO
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const { currentUser } = useUser();
  const createMutation = useCreateTodo();
  const { confirm } = useDialog();
  const { data: todoLists = [] } = useTodoLists();
  const { data: users = [] } = useUsers();

  // Show input when clicked
  const handleShow = () => {
    setIsVisible(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // Hide input when escape is pressed or input loses focus
  const handleHide = () => {
    if (!inputValue.trim()) {
      setIsVisible(false);
    }
  };

  const restoreFocus = () => {
    // Use setTimeout to focus after state updates and DOM updates complete
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      handleCreateTodo();
    } else if (e.key === 'Escape') {
      setInputValue('');
      setIsVisible(false);
    }
  };

  const getDueDateForSection = () => {
    switch (sectionType) {
      case 'today':
        return getTodayISO();
      case 'tomorrow':
        return getTomorrowISO();
      case 'thisWeekend':
        return getThisWeekendISO();
      case 'overdue':
        // For overdue, set yesterday as due date
        return getYesterdayISO();
      case 'later':
        return getRandomLaterDate();
      case 'noDueDate':
      default:
        return null;
    }
  };
  const getEffectiveDueDate = () => {
    if (typeof dueDateISO !== 'undefined') {
      return dueDateISO;
    }
    return getDueDateForSection();
  };

  const getAssigneeFromFilters = () => {
    if (!currentFilters?.assignee || currentFilters.assignee === 'any') {
      return '';
    } else if (currentFilters.assignee === 'unassigned') {
      return '';
    } else {
      return currentFilters.assignee;
    }
  };

  const getAssigneeDisplayName = (email: string) => {
    if (!email) return null;
    if (currentUser && email === currentUser.email) return 'Me';
    const user = users.find(u => u.email === email);
    return user?.name || email.split('@')[0];
  };

  const getListIdFromFilters = () => {
    if (typeof currentFilters?.list === 'number') {
      return currentFilters.list;
    }
    // If "all lists" is selected, use the default list if it exists, otherwise first available list
    if (currentFilters?.list === 'all' && todoLists.length > 0) {
      // Prioritize the default list (created during registration)
      const defaultList = todoLists.find((l: TodoList) => l.isDefault);
      if (defaultList) {
        return defaultList.id;
      }
      // Fallback to first list
      return todoLists[0].id;
    }
    // Return undefined to trigger auto-creation of default list in useCreateTodo hook
    return undefined;
  };

  const handleCreateTodo = async () => {
    if (!inputValue.trim() || !currentUser?.email) return;

    const value = inputValue.trim();
    const assignedTo = getAssigneeFromFilters();
    const listId = getListIdFromFilters();
  const dueDate = getEffectiveDueDate();

    setInputValue(''); // Clear immediately for fast entry
    restoreFocus(); // Restore focus after clearing

    // Handle list sharing if assigning to someone else (only if we have a specific list)
    const list = typeof listId === 'number' ? todoLists.find((l: TodoList) => l.id === listId) : null;
    if (
      assignedTo &&
      list &&
      assignedTo !== currentUser?.email &&
      assignedTo !== list.createdBy &&
      !list.sharedWith.includes(assignedTo)
    ) {
      confirm(
        list.isShared ? 'Share List' : 'List is Private',
        list.isShared
          ? `The list "${list.name}" is shared, but not with this user. Share the list with ${assignedTo} and assign the task?`
          : `The list "${list.name}" is private. Share the list with ${assignedTo} and assign the task?`,
        async () => {
          await todoApi.shareListWithUser(list.id, assignedTo, list.sharedWith, list.createdBy);
          createMutation.mutate({
            title: value,
            createdBy: currentUser?.email,
            assignedTo,
            listId,
            priority: 'Medium',
            dueDate,
          }, {
            onSuccess: (newTodo) => {
              restoreFocus();
              
              // If a new list was auto-created, update the filter
              if (
                listId === undefined && 
                newTodo.listId && 
                currentFilters?.list === 'all' &&
                onFiltersChange
              ) {
                onFiltersChange({
                  assignee: currentFilters.assignee,
                  list: newTodo.listId,
                  completed: currentFilters.completed,
                  tags: currentFilters.tags,
                  search: currentFilters.search
                });
              }
              
              if (onNewTodoCreated && newTodo.id && newTodo.title) {
                onNewTodoCreated(newTodo.id, newTodo.title);
              }
            },
            onError: (error) => {
              console.error('Failed to create todo:', error);
              restoreFocus();
            }
          });
        }
      );
      return;
    }

    createMutation.mutate({
      title: value,
      createdBy: currentUser?.email,
      assignedTo,
      listId,
      priority: 'Medium',
      dueDate,
    }, {
      onSuccess: (newTodo) => {
        restoreFocus();
        
        // If a new list was auto-created (listId was undefined and now we have a todo with a listId)
        // and we're currently filtering by "all", switch to show that specific list
        if (
          listId === undefined && 
          newTodo.listId && 
          currentFilters?.list === 'all' &&
          onFiltersChange
        ) {
          onFiltersChange({
            assignee: currentFilters.assignee,
            list: newTodo.listId,
            completed: currentFilters.completed,
            tags: currentFilters.tags,
            search: currentFilters.search
          });
        }
        
        if (onNewTodoCreated && newTodo.id && newTodo.title) {
          onNewTodoCreated(newTodo.id, newTodo.title);
        }
      },
      onError: (error) => {
        console.error('Failed to create todo:', error);
        restoreFocus();
      }
    });
  };

  const getPlaceholderContent = useMemo(() => {
    const assigneeEmail = getAssigneeFromFilters();
    const assigneeDisplay = assigneeEmail ? getAssigneeDisplayName(assigneeEmail) : null;
    // If a fixed date is provided, bias the prompts around that date
    const hasFixedDate = typeof dueDateISO !== 'undefined';

    const dayLabel = (() => {
      if (!hasFixedDate || !dueDateISO) return null;
      const date = new Date(dueDateISO);
      if (isNaN(date.getTime())) return null;
      const today = new Date();
      const d0 = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
      const d1 = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
      const diffDays = Math.round((d1 - d0) / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return 'today';
      if (diffDays === 1) return 'tomorrow';
      if (diffDays === -1) return 'yesterday';
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    })();

    const baseTexts = {
      'today': ['What needs to get done today?', 'Ready to tackle today?', "What's on the agenda?"],
      'tomorrow': ["Planning ahead for tomorrow?", "What's tomorrow's priority?", 'Get a head start on tomorrow...'],
      'thisWeekend': ['Weekend plans? 🌴', "What's on the weekend agenda?", 'Time to relax and plan...', 'Weekend vibes incoming! ✨'],
      'later': ["What's coming up later?", 'Future planning time...', "What's on the horizon?"],
      'overdue': ['Time to catch up...', "Let's get back on track", 'What needs attention?'],
      'noDueDate': ["What's on your mind?", 'Capture that thought...', 'What needs to be done?']
    } as const;

    const dateAwareTexts = hasFixedDate
      ? [
          `What needs to get done ${dayLabel}?`,
          `Plan something for ${dayLabel}…`,
          `Add a task for ${dayLabel}`,
          `What’s on ${dayLabel}?`,
        ]
      : [];

    const texts = [
      ...(baseTexts[sectionType] || ['Add a new task...']),
      ...dateAwareTexts,
    ];

    const randomText = texts[Math.floor(Math.random() * texts.length)];

    return {
      mainText: randomText,
      assignee: assigneeDisplay && assigneeDisplay !== 'Me' ? assigneeDisplay : null
    };
  }, [
    sectionType, 
    currentFilters?.assignee, 
    currentFilters?.list,
    currentUser?.email,
    users,
    dueDateISO // Include due date override to vary placeholder
  ]);

  if (!isVisible) {
    const { mainText, assignee } = getPlaceholderContent;
    
    return (
      <div className={`section-todo-entry-placeholder ${hideIcon ? 'no-icon' : ''}`} onClick={handleShow}>
        {!hideIcon && <Plus size={16} />}
        <span className="placeholder-main-text">{mainText}</span>
        {assignee && (
          <span className="placeholder-assignee">for {assignee}</span>
        )}
      </div>
    );
  }

  return (
    <div className="section-todo-entry">
      <input
        ref={inputRef}
        type="text"
        placeholder={getPlaceholderContent.mainText}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleHide}
        className="section-todo-input"
        maxLength={200}
      />
      {getPlaceholderContent.assignee && (
        <span className="placeholder-assignee" onMouseDown={(e) => e.preventDefault()}>for {getPlaceholderContent.assignee}</span>
      )}
      <button 
        className="section-todo-add-btn"
        onClick={handleCreateTodo}
        disabled={!inputValue.trim()}
      >
        ↵
      </button>
    </div>
  );
};